import {
    _generateNumericShortCodeFromShortCodeString,
    _generateShortCodeStringFromNumericShortCode,
} from "./ConvertNumericShortCodesToStrings.js";
import {
    getRelevantShortCodeAddressRangeFromEui,
    getRelevantShortCodeAddressRangeFromShortCode,
} from "./shortCodeAddressRanges.js";
import { EUI_STRING_LENGTH } from "./shortCodesLookup.js";
import { cleanUpEui, isValidShortCode } from "./shortCodeValidation.js";

/**
 * Generates a short code string from an EUI by finding the appropriate address range
 * and converting the numeric value.
 *
 * Remaining display-oriented callers that still generate short codes instead of reading stored
 * verdiIds are tracked in SWD-3138 follow-up (sprout debugger recent-target cache, QR scan before a
 * device exists, and the Slack `/d` fallback).
 *
 * @param eui - The EUI string to convert to a short code
 * @returns The generated short code, or "???-???" if conversion fails due to an unknown address range
 * @throws Error if the provided EUI is not the expected length after normalization
 */
export function generateShortCodeFromEui(eui: string): string {
    const filteredEui: string = cleanUpEui(eui);

    if (filteredEui.length < EUI_STRING_LENGTH) {
        throw new Error(
            `Eui provided (${eui} which was filtered down to ${filteredEui}) was not the correct length. Should be 16 alphanumeric characters`,
        );
    }

    const relevantAddressRange = getRelevantShortCodeAddressRangeFromEui(filteredEui);

    if (!relevantAddressRange) {
        return "???-???";
    }

    const numericEui = BigInt(`0x${filteredEui.replace("eui-", "")}`);

    const numericShortCode =
        numericEui - relevantAddressRange.startingEuiNumeric + relevantAddressRange.startingShortCodeNumeric;

    const shortCode = _generateShortCodeStringFromNumericShortCode(
        Number(numericShortCode),
        relevantAddressRange.doLegacyRudeWordReplacement,
    );

    return shortCode ?? "???-???";
}

/**
 * Normalizes an EUI-ish string (e.g. a device node_id) into the canonical `eui-`-prefixed form used for the
 * AssetIdentity `loraEui` field.
 *
 * Note: this function does not validate that the result matches `loraEuiRegex` (e.g. length/hex).
 *
 * @param eui - The EUI string, with or without the "eui-" prefix / separators
 * @returns The normalized `eui-`-prefixed EUI string
 */
export function toLoraEui(eui: string): string {
    return `eui-${cleanUpEui(eui)}`;
}

/**
 * Converts a short code back to its corresponding EUI string.
 *
 * @param shortCode - The short code to convert
 * @returns The EUI string corresponding to the short code
 * @throws Error if the short code is invalid or not in a known range
 */
export function generateEuiFromShortCode(shortCode: string): string {
    if (!isValidShortCode(shortCode)) {
        throw new Error(`Short code ${shortCode} is not valid`);
    }

    const addressRange = getRelevantShortCodeAddressRangeFromShortCode(shortCode);

    // this will be between 0 and the maxPossibleValue, but won't be the whole EUI of the device
    const numericShortCode = _generateNumericShortCodeFromShortCodeString(shortCode);

    const euiNumber =
        BigInt(numericShortCode) - addressRange.startingShortCodeNumeric + addressRange.startingEuiNumeric;

    return `eui-${euiNumber.toString(16).toLowerCase().padStart(EUI_STRING_LENGTH, "0")}`;
}

/**
 * Checks if an EUI is valid by verifying its format and ensuring it falls within a known address range
 * @param eui The EUI to validate
 * @returns boolean indicating if the EUI is valid
 */
export function isValidEui(eui: string): boolean {
    try {
        const cleanedEui = cleanUpEui(eui);

        if (cleanedEui.length !== EUI_STRING_LENGTH) {
            return false;
        }

        const relevantAddressRange = getRelevantShortCodeAddressRangeFromEui(cleanedEui);

        if (!relevantAddressRange) {
            return false;
        }

        // Check that we can generate a shortcode that's not the error value
        const attemptedConversion = generateShortCodeFromEui(eui);
        return attemptedConversion !== "???-???";
    } catch (e) {
        return false;
    }
}
